import {useEffect, useState} from "react";

import {Tabs} from "antd";

import {AttachedDocuments} from "./AttachedDocuments/AttachedDocuments.jsx";
import {CommonalityTable} from "./CommonalityTable.jsx";
import {HersTable} from "./HersTable.jsx";
import {PM02ProjectionTab} from "./PM02ProjectionTab.jsx";
import {ReservationsTab} from "./ReservationsTab.jsx";
import {SourcingTab} from "./SourcingTab.jsx";
import {StockOnHandTab} from "./StockOnHandTab/StockOnHandTab.jsx";
import {StructuredData} from "./StructuredData.jsx";
import {TransactionsTab} from "./TransactionsTab/TransactionsTab.jsx";
import {UsageTab} from "./UsageTab/UsageTab.jsx";
import {WhereUsedTable} from "./WhereUsedTable.jsx";
import {PlantInfoTab} from "./PlantInfoTab.jsx";
import {allowAddingDocuments, showAnalyticsTabs} from "@/properties.js";
import {
    PM02Projection,
    Reservations,
    Sourcing,
    PlantInfo,
    HersRecords,
    Description,
    Usage,
    WhereUsed,
    MaterialTabs,
} from '@/variants/index.js';
import {MATERIAL_TAB_KEYS} from "@/pages/additional-data/lib/TabDefinitions.js";
import api from "@/api.js";
import {useSearchParams} from "react-router-dom";

export const MaterialInfoTabs = ({
	item,
	usageInfo,
	transactionsInfo,
	sourcingInfo,
	documentsList = [],
	updateDocsTable,
	hers = [],
	commonality,
	stockData,
	calcStockData,
	equipment,
	structured,
	dashboardSummary,
	focusSummary,
	reservations,
	pm02Data,
	onUsageChange,
	onTransactionsChange,
	totalUsageCount,
	loading,
}) => {
	const [activeKey, setActiveKey] = useState(MATERIAL_TAB_KEYS.STOCK_ON_HAND);
	const [savedDefaultTab, setSavedDefaultTab] = useState(null);
	const [defaultTabLoaded, setDefaultTabLoaded] = useState(false);

	const [searchParams] = useSearchParams();
	const urlTabParam = searchParams.get("tab");

	useEffect(() => {
		api.get("/settings/table-overridden?name=Default Material Tab", { omitGlobalError: true })
			.then(json => {
				if (json.data && json.data.length > 0) {
					setSavedDefaultTab(json.data[0]);
				}
			})
			.catch(console.log)
			.finally(() => setDefaultTabLoaded(true));
	}, []);

	useEffect(() => {
		if (!defaultTabLoaded) return;

		if (urlTabParam) {
			setActiveKey(urlTabParam);
		} else if (savedDefaultTab) {
			if (savedDefaultTab === MATERIAL_TAB_KEYS.DESCRIPTION && (!structured || structured.length === 0)) {
				setActiveKey(MATERIAL_TAB_KEYS.STOCK_ON_HAND);
			} else {
				setActiveKey(savedDefaultTab);
			}
		} else {
			setActiveKey(MATERIAL_TAB_KEYS.STOCK_ON_HAND);
		}
	}, [defaultTabLoaded, savedDefaultTab, structured]);

	const tabConfig = {
		[MATERIAL_TAB_KEYS.DESCRIPTION]: {
			key: MATERIAL_TAB_KEYS.DESCRIPTION,
			label: <span data-testid="material-info-tab-description">{Description.tabTitle}</span>,
			children: <StructuredData structured={structured} />,
			isEnabled: structured && structured.length > 0,
		},
		[MATERIAL_TAB_KEYS.STOCK_ON_HAND]: {
			key: MATERIAL_TAB_KEYS.STOCK_ON_HAND,
			label: <span data-testid="material-info-tab-stock-on-hand">Stock on Hand</span>,
			children: <StockOnHandTab data={stockData} item={item} calcStockData={calcStockData} />,
			isEnabled: true,
		},
		[MATERIAL_TAB_KEYS.PLANT_INFO]: {
			key: MATERIAL_TAB_KEYS.PLANT_INFO,
			label: <span data-testid="material-info-tab-plant-info">Plant Info</span>,
			children: <PlantInfoTab data={stockData} />,
			isEnabled: PlantInfo.enabled,
		},
		[MATERIAL_TAB_KEYS.USAGE]: {
			key: MATERIAL_TAB_KEYS.USAGE,
			label: <span data-testid="material-info-tab-usage">{`${Usage.tabTitle} (${totalUsageCount || 0})`}</span>,
			children: <UsageTab item={item} usageInfo={usageInfo} onUsageChange={onUsageChange} loading={loading} />,
			isEnabled: true,
		},
		[MATERIAL_TAB_KEYS.WHERE_USED]: {
			key: MATERIAL_TAB_KEYS.WHERE_USED,
			label: <span data-testid="material-info-tab-where-used">{`${WhereUsed.tabTitle} (${equipment.length || 0})`}</span>,
			children: <WhereUsedTable equipment={equipment} />,
			isEnabled: true,
		},
		[MATERIAL_TAB_KEYS.DASHBOARD_SUMMARY]: {
			key: MATERIAL_TAB_KEYS.DASHBOARD_SUMMARY,
			label: <span data-testid="material-info-tab-dashboard-summary">Dashboard Summary</span>,
			children: <StructuredData structured={dashboardSummary} />,
			isEnabled: showAnalyticsTabs,
		},
		[MATERIAL_TAB_KEYS.FOCUS_SUMMARY]: {
			key: MATERIAL_TAB_KEYS.FOCUS_SUMMARY,
			label: <span data-testid="material-info-tab-focus-summary">Focus Summary</span>,
			children: <StructuredData structured={focusSummary} />,
			isEnabled: showAnalyticsTabs,
		},
		[MATERIAL_TAB_KEYS.TRANSACTIONS]: {
			key: MATERIAL_TAB_KEYS.TRANSACTIONS,
			label: (
				<span data-testid="material-info-tab-transactions">{`Transactions (${(transactionsInfo.tableData && transactionsInfo.tableData.length) || 0})`}</span>
			),
			children: (
				<TransactionsTab
					transactionsInfo={transactionsInfo}
					item={item}
					onTransactionsChange={onTransactionsChange}
				/>
			),
			isEnabled: true,
		},
		[MATERIAL_TAB_KEYS.RESERVATIONS]: {
			key: MATERIAL_TAB_KEYS.RESERVATIONS,
			label: (
				<span data-testid="material-info-tab-reservations">{`Reservations (${reservations.length || 0})`}</span>
			),
			children: <ReservationsTab reservations={reservations} />,
			isEnabled: Reservations.enabled,
		},
		[MATERIAL_TAB_KEYS.SOURCING]: {
			key: MATERIAL_TAB_KEYS.SOURCING,
			label: (
				<span data-testid="material-info-tab-sourcing">{`Sourcing (${(sourcingInfo && sourcingInfo.length) || 0})`}</span>
			),
			children: <SourcingTab sourcingInfo={sourcingInfo} />,
			isEnabled: Sourcing.enabled,
		},
		[MATERIAL_TAB_KEYS.PM02_PROJECTION]: {
			key: MATERIAL_TAB_KEYS.PM02_PROJECTION,
			label: (
				<span data-testid="material-info-tab-pm02">
					{`${PM02Projection.tabTitle} (${(pm02Data && pm02Data.length) || 0})`}
				</span>
			),
			children: <PM02ProjectionTab pm02Data={pm02Data} />,
			isEnabled: PM02Projection.enabled,
		},
		[MATERIAL_TAB_KEYS.POTENTIAL_DUPLICATES]: {
			key: MATERIAL_TAB_KEYS.POTENTIAL_DUPLICATES,
			label: (
				<span data-testid="material-info-tab-potential-duplicates">{`Potential Duplicates (${commonality.length || 0})`}</span>
			),
			children: <CommonalityTable item={item} commonality={commonality} />,
			isEnabled: true,
		},
		[MATERIAL_TAB_KEYS.ATTACHED_DOCUMENTS]: {
			key: MATERIAL_TAB_KEYS.ATTACHED_DOCUMENTS,
			label: (
				<span data-testid="material-info-tab-attached-documents">{`Attached Documents (${documentsList.length || 0})`}</span>
			),
			children: <AttachedDocuments item={item} documentsList={documentsList} updateDocsTable={updateDocsTable} />,
			isEnabled: allowAddingDocuments,
		},
		[MATERIAL_TAB_KEYS.HERS_RECORDS]: {
			key: MATERIAL_TAB_KEYS.HERS_RECORDS,
			label: (
				<span data-testid="material-info-tab-hers-records">{`${HersRecords.tabTitle} (${hers.length || 0})`}</span>
			),
			children: <HersTable hers={hers} />,
			isEnabled: HersRecords.enabled,
		},
	};

	const tabItems = MaterialTabs.tabOrder.map(key => tabConfig[key]).filter(tab => Boolean(tab.isEnabled));

	return (
		<Tabs
			defaultActiveKey={MATERIAL_TAB_KEYS.DESCRIPTION}
			activeKey={activeKey}
			onChange={setActiveKey}
			data-testid="material-info-tabs"
			items={tabItems}
		/>
	);
};
